import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrencyForPDF } from './calculations';

// Alias for cleaner code
const formatCurrency = formatCurrencyForPDF;

// Common PDF header
const addHeader = (doc, title, subtitle = 'Indian Financial Calculator') => {
  // Background header
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, 210, 40, 'F');
  
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 105, 18, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle, 105, 28, { align: 'center' });
  
  // Date
  doc.setFontSize(10);
  doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 35, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
};

// Common PDF footer
const addFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      'Disclaimer: This is for informational purposes only. Please consult a financial advisor for professional advice.',
      105,
      285,
      { align: 'center' }
    );
    doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
  }
};

// EMI Calculator PDF
export const generateEMIPDF = (data) => {
  const doc = new jsPDF();
  addHeader(doc, 'EMI Calculation Report');
  const modeLabel = data.mode === 'flat'
    ? 'Flat Rate'
    : data.mode === 'floating'
      ? 'Floating Rate'
      : 'Reducing Balance';
  
  let y = 50;
  
  // Summary Box
  doc.setFillColor(248, 250, 252);
  doc.rect(14, y, 182, 45, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(14, y, 182, 45, 'S');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text('Loan Summary', 20, y + 10);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  
  const summaryItems = [
    ['Calculation Type:', modeLabel],
    ['Loan Amount:', formatCurrency(data.principal)],
    ['Interest Rate:', `${data.rate}% p.a.`],
    ['Loan Tenure:', `${data.tenure} months (${(data.tenure / 12).toFixed(1)} years)`],
    ['Monthly EMI:', formatCurrency(data.emi)],
  ];

  if (data.mode === 'floating' && data.revisedEmi) {
    summaryItems.push(['Revised EMI:', formatCurrency(data.revisedEmi)]);
  }
  
  let col1 = 20, col2 = 110;
  summaryItems.forEach((item, index) => {
    const yPos = y + 20 + (index * 7);
    doc.setFont('helvetica', 'normal');
    doc.text(item[0], index < 2 ? col1 : col2, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(item[1], index < 2 ? col1 + 35 : col2 + 35, yPos);
  });
  
  y += 55;
  
  // Payment Breakdown
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text('Payment Breakdown', 20, y);
  
  y += 10;
  
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: [
      ['Principal Amount', formatCurrency(data.principal)],
      ['Total Interest', formatCurrency(data.totalInterest)],
      ['Total Payment', formatCurrency(data.totalPayment)],
    ],
    styles: { fontSize: 11 },
    headStyles: { fillColor: [37, 99, 235] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });
  
  y = doc.lastAutoTable.finalY + 15;
  
  // Amortization Schedule (first 12 months)
  if (data.schedule && data.schedule.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Amortization Schedule (First 12 Months)', 20, y);
    
    y += 10;
    
    const scheduleData = data.schedule.slice(0, 12).map(row => [
      row.month,
      formatCurrency(row.emi),
      formatCurrency(row.principal),
      formatCurrency(row.interest),
      formatCurrency(row.balance),
    ]);
    
    autoTable(doc, {
      startY: y,
      head: [['Month', 'EMI', 'Principal', 'Interest', 'Balance']],
      body: scheduleData,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [37, 99, 235] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
  }
  
  addFooter(doc);
  doc.save('EMI_Calculation_Report.pdf');
};

// SIP Calculator PDF
export const generateSIPPDF = (data) => {
  const doc = new jsPDF();
  addHeader(doc, 'SIP Investment Report');
  
  let y = 50;
  
  // Investment Summary
  doc.setFillColor(248, 250, 252);
  doc.rect(14, y, 182, 50, 'F');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text('Investment Summary', 20, y + 10);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  
  const items = [
    ['Monthly Investment:', formatCurrency(data.monthlyInvestment)],
    ['Expected Return:', `${data.expectedReturn}% p.a.`],
    ['Investment Period:', `${data.years} years`],
  ];
  
  items.forEach((item, index) => {
    doc.text(item[0], 20, y + 22 + (index * 8));
    doc.setFont('helvetica', 'bold');
    doc.text(item[1], 70, y + 22 + (index * 8));
    doc.setFont('helvetica', 'normal');
  });
  
  y += 60;
  
  // Results
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: [
      ['Total Investment', formatCurrency(data.totalInvested)],
      ['Estimated Returns', formatCurrency(data.wealthGained)],
      ['Total Value', formatCurrency(data.futureValue)],
    ],
    styles: { fontSize: 12 },
    headStyles: { fillColor: [37, 99, 235] },
    columnStyles: {
      1: { fontStyle: 'bold' }
    }
  });
  
  addFooter(doc);
  doc.save('SIP_Investment_Report.pdf');
};

// PPF Calculator PDF
export const generatePPFPDF = (data) => {
  const doc = new jsPDF();
  addHeader(doc, 'PPF Calculator Report');
  
  let y = 50;
  
  // Summary
  doc.setFillColor(248, 250, 252);
  doc.rect(14, y, 182, 40, 'F');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text('PPF Summary', 20, y + 10);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  
  doc.text(`Yearly Investment: ${formatCurrency(data.yearlyInvestment)}`, 20, y + 22);
  doc.text(`Investment Period: ${data.years} years`, 20, y + 30);
  doc.text(`Interest Rate: ${data.interestRate}% p.a.`, 110, y + 22);
  
  y += 50;
  
  // Results Table
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: [
      ['Total Investment', formatCurrency(data.totalInvested)],
      ['Total Interest Earned', formatCurrency(data.totalInterest)],
      ['Maturity Amount', formatCurrency(data.maturityAmount)],
    ],
    styles: { fontSize: 12 },
    headStyles: { fillColor: [37, 99, 235] },
  });
  
  y = doc.lastAutoTable.finalY + 15;
  
  // Yearly Breakdown
  if (data.yearlyData && data.yearlyData.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Year-wise Breakdown', 20, y);
    
    y += 10;
    
    autoTable(doc, {
      startY: y,
      head: [['Year', 'Investment', 'Interest', 'Balance']],
      body: data.yearlyData.map(row => [
        row.year,
        formatCurrency(row.investment),
        formatCurrency(row.interest),
        formatCurrency(row.balance),
      ]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [37, 99, 235] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
  }
  
  addFooter(doc);
  doc.save('PPF_Calculation_Report.pdf');
};

// Income Tax PDF
export const generateIncomeTaxPDF = (data, regime) => {
  const doc = new jsPDF();
  addHeader(doc, `Income Tax Report (${regime} Regime)`);
  
  let y = 50;
  
  // Income Summary
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text('Income Tax Summary', 20, y);
  
  y += 10;
  
  const summaryData = [
    ['Gross Income', formatCurrency(data.grossIncome)],
    ['Deductions', formatCurrency(data.totalDeductions || data.standardDeduction)],
    ['Taxable Income', formatCurrency(data.taxableIncome)],
    ['Tax Before Rebate', formatCurrency(data.taxBeforeRebate)],
    ['Rebate u/s 87A', formatCurrency(data.rebate)],
    ['Tax After Rebate', formatCurrency(data.taxAfterRebate)],
    ['Health & Education Cess (4%)', formatCurrency(data.cess)],
    ['Total Tax Payable', formatCurrency(data.totalTax)],
  ];
  
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: summaryData,
    styles: { fontSize: 11 },
    headStyles: { fillColor: [37, 99, 235] },
    columnStyles: {
      1: { halign: 'right' }
    }
  });
  
  y = doc.lastAutoTable.finalY + 15;
  
  // Tax Slab Breakdown
  if (data.breakdown && data.breakdown.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Tax Slab Breakdown', 20, y);
    
    y += 10;
    
    autoTable(doc, {
      startY: y,
      head: [['Income Slab', 'Rate', 'Taxable Amount', 'Tax']],
      body: data.breakdown.map(row => [
        row.slab,
        row.rate,
        formatCurrency(row.taxableAmount),
        formatCurrency(row.tax),
      ]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [37, 99, 235] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
  }
  
  addFooter(doc);
  doc.save(`Income_Tax_Report_${regime}_Regime.pdf`);
};

// GST Calculator PDF
export const generateGSTPDF = (data) => {
  const doc = new jsPDF();
  addHeader(doc, 'GST Calculation Report');
  
  let y = 50;
  
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: [
      ['Base Amount', formatCurrency(data.baseAmount)],
      [`CGST (${data.gstRate / 2}%)`, formatCurrency(data.cgst)],
      [`SGST (${data.gstRate / 2}%)`, formatCurrency(data.sgst)],
      [`Total GST (${data.gstRate}%)`, formatCurrency(data.gstAmount)],
      ['Total Amount', formatCurrency(data.totalAmount)],
    ],
    styles: { fontSize: 12 },
    headStyles: { fillColor: [37, 99, 235] },
  });
  
  addFooter(doc);
  doc.save('GST_Calculation_Report.pdf');
};

// Salary Slip PDF
export const generateSalarySlipPDF = (data) => {
  const doc = new jsPDF();
  const { employeeDetails, companyDetails, result, payPeriod } = data;
  
  // Format pay period
  const formatMonth = (dateStr) => {
    if (!dateStr) return 'N/A';
    const [year, month] = dateStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };
  
  // Header with company details
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, 210, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(companyDetails.name || 'Company Name', 105, 14, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(companyDetails.address || 'Company Address', 105, 22, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`SALARY SLIP - ${formatMonth(payPeriod)}`, 105, 30, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  
  let y = 45;
  
  // Employee Details Box
  doc.setFillColor(248, 250, 252);
  doc.rect(14, y, 182, 40, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(14, y, 182, 40, 'S');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text('Employee Details', 20, y + 8);
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  
  const empDetails = [
    ['Name:', employeeDetails.name || 'N/A', 'Employee ID:', employeeDetails.employeeId || 'N/A'],
    ['Designation:', employeeDetails.designation || 'N/A', 'Department:', employeeDetails.department || 'N/A'],
    ['PAN:', employeeDetails.panNumber || 'N/A', 'Bank A/C:', employeeDetails.bankAccount || 'N/A'],
  ];
  
  empDetails.forEach((row, index) => {
    doc.setFont('helvetica', 'normal');
    doc.text(row[0], 20, y + 18 + (index * 7));
    doc.setFont('helvetica', 'bold');
    doc.text(row[1], 50, y + 18 + (index * 7));
    doc.setFont('helvetica', 'normal');
    doc.text(row[2], 110, y + 18 + (index * 7));
    doc.setFont('helvetica', 'bold');
    doc.text(row[3], 145, y + 18 + (index * 7));
  });
  
  y += 50;
  
  // Earnings and Deductions side by side
  const earningsData = result.earningsBreakdown.map(e => [e.name, formatCurrency(e.amount)]);
  earningsData.push(['', '']);
  earningsData.push(['Gross Salary', formatCurrency(result.grossSalary)]);
  
  const deductionsData = result.deductionsBreakdown.map(d => [d.name, formatCurrency(d.amount)]);
  deductionsData.push(['', '']);
  deductionsData.push(['Total Deductions', formatCurrency(result.totalDeductions)]);
  
  // Make both tables same length
  while (earningsData.length < deductionsData.length) {
    earningsData.splice(earningsData.length - 2, 0, ['', '']);
  }
  while (deductionsData.length < earningsData.length) {
    deductionsData.splice(deductionsData.length - 2, 0, ['', '']);
  }
  
  // Earnings Table
  autoTable(doc, {
    startY: y,
    head: [['Earnings', 'Amount (Rs.)']],
    body: earningsData,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 55 },
      1: { cellWidth: 30, halign: 'right' },
    },
    margin: { left: 14 },
    tableWidth: 88,
    didParseCell: function(data) {
      if (data.row.index === earningsData.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [209, 250, 229];
      }
    },
  });
  
  // Deductions Table
  autoTable(doc, {
    startY: y,
    head: [['Deductions', 'Amount (Rs.)']],
    body: deductionsData,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [239, 68, 68], textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 55 },
      1: { cellWidth: 30, halign: 'right' },
    },
    margin: { left: 108 },
    tableWidth: 88,
    didParseCell: function(data) {
      if (data.row.index === deductionsData.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [254, 202, 202];
      }
    },
  });
  
  y = doc.lastAutoTable.finalY + 15;
  
  // Net Salary Box
  doc.setFillColor(16, 185, 129);
  doc.rect(14, y, 182, 25, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('NET SALARY (TAKE HOME)', 20, y + 10);
  
  doc.setFontSize(18);
  doc.text(formatCurrency(result.netSalary), 186, y + 16, { align: 'right' });
  
  y += 35;
  
  // Amount in Words
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Amount in Words: ${numberToWords(result.netSalary)} Only`, 14, y);
  
  y += 15;
  
  // Employer Contributions (if any)
  if (result.employerPF > 0) {
    doc.setFillColor(248, 250, 252);
    doc.rect(14, y, 182, 20, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(14, y, 182, 20, 'S');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Employer Contributions:', 20, y + 8);
    doc.setFont('helvetica', 'normal');
    doc.text(`PF: ${formatCurrency(result.employerPF)}`, 20, y + 15);
    
    doc.setFont('helvetica', 'bold');
    doc.text(`CTC: ${formatCurrency(result.ctc)}`, 150, y + 12);
    
    y += 30;
  }
  
  // Footer
  y = 250;
  doc.setDrawColor(200, 200, 200);
  doc.line(14, y, 196, y);
  
  y += 10;
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('This is a computer-generated salary slip and does not require a signature.', 105, y, { align: 'center' });
  
  y += 15;
  doc.setFontSize(8);
  doc.text('Employer Signature', 50, y, { align: 'center' });
  doc.text('Employee Signature', 160, y, { align: 'center' });
  
  doc.line(20, y - 5, 80, y - 5);
  doc.line(130, y - 5, 190, y - 5);
  
  // Date
  doc.setFontSize(8);
  doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 290, { align: 'center' });
  
  doc.save(`Salary_Slip_${formatMonth(payPeriod).replace(/\s+/g, '_')}.pdf`);
};

// Helper function to convert number to words
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 
                'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 
                'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const numToWords = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
    if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
    if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
    return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
  };
  
  return 'Rupees ' + numToWords(Math.round(num));
};

// Helper function to convert ₹ symbol to Rs. for PDF compatibility
const sanitizeForPDF = (value) => {
  if (typeof value === 'string') {
    return value.replace(/₹/g, 'Rs.');
  }
  return value;
};

// Generic Calculator PDF
export const generateGenericPDF = (title, data) => {
  const doc = new jsPDF();
  addHeader(doc, title);
  
  let y = 50;
  
  const tableData = Object.entries(data).map(([key, value]) => {
    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    let formattedValue;
    if (typeof value === 'number') {
      formattedValue = value > 1000 ? formatCurrency(value) : value.toString();
    } else {
      formattedValue = sanitizeForPDF(value);
    }
    return [label, formattedValue];
  });
  
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Value']],
    body: tableData,
    styles: { fontSize: 12 },
    headStyles: { fillColor: [37, 99, 235] },
  });
  
  addFooter(doc);
  doc.save(`${title.replace(/\s+/g, '_')}_Report.pdf`);
};

// Invoice/Bill PDF Generator
export const generateInvoicePDF = (data) => {
  const doc = new jsPDF();
  const {
    invoiceType,
    invoiceNumber,
    invoiceDate,
    dueDate,
    businessDetails,
    customerDetails,
    items,
    totals,
    discount,
    bankDetails,
    notes,
    terms,
  } = data;

  const invoiceTypeLabels = {
    invoice: 'TAX INVOICE',
    bill: 'BILL OF SUPPLY',
    quotation: 'QUOTATION',
    estimate: 'ESTIMATE',
    proforma: 'PROFORMA INVOICE',
  };

  // Header
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, 210, 10, 'F');

  let y = 20;

  // Business Name (Large)
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  doc.text(businessDetails.name || 'Your Business Name', 14, y);

  // Invoice Type Badge
  doc.setFillColor(37, 99, 235);
  doc.roundedRect(140, y - 8, 56, 12, 2, 2, 'F');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(invoiceTypeLabels[invoiceType] || 'INVOICE', 168, y - 1, { align: 'center' });

  y += 8;

  // Business Details
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  
  if (businessDetails.address) {
    doc.text(businessDetails.address, 14, y);
    y += 4;
  }
  if (businessDetails.city || businessDetails.state) {
    doc.text(`${businessDetails.city}${businessDetails.city && businessDetails.state ? ', ' : ''}${businessDetails.state} ${businessDetails.pincode || ''}`.trim(), 14, y);
    y += 4;
  }
  if (businessDetails.phone) {
    doc.text(`Phone: ${businessDetails.phone}`, 14, y);
    y += 4;
  }
  if (businessDetails.email) {
    doc.text(`Email: ${businessDetails.email}`, 14, y);
    y += 4;
  }
  if (businessDetails.gstin) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text(`GSTIN: ${businessDetails.gstin}`, 14, y);
    y += 4;
  }
  if (businessDetails.pan) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`PAN: ${businessDetails.pan}`, 14, y);
  }

  // Invoice Details (Right side)
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text(`Invoice No: ${invoiceNumber}`, 196, 28, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${formatDate(invoiceDate)}`, 196, 34, { align: 'right' });
  if (dueDate) {
    doc.text(`Due Date: ${formatDate(dueDate)}`, 196, 40, { align: 'right' });
  }

  y = Math.max(y, 50) + 10;

  // Divider
  doc.setDrawColor(226, 232, 240);
  doc.line(14, y, 196, y);
  y += 10;

  // Bill To Section
  doc.setFillColor(248, 250, 252);
  doc.rect(14, y, 85, 35, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(14, y, 85, 35, 'S');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 100, 100);
  doc.text('BILL TO:', 18, y + 6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(customerDetails.name || 'Customer Name', 18, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  let custY = y + 18;
  if (customerDetails.address) {
    doc.text(customerDetails.address, 18, custY);
    custY += 4;
  }
  if (customerDetails.city || customerDetails.state) {
    doc.text(`${customerDetails.city}${customerDetails.city && customerDetails.state ? ', ' : ''}${customerDetails.state}`.trim(), 18, custY);
    custY += 4;
  }
  if (customerDetails.phone) {
    doc.text(`Ph: ${customerDetails.phone}`, 18, custY);
    custY += 4;
  }
  if (customerDetails.gstin) {
    doc.setFont('helvetica', 'bold');
    doc.text(`GSTIN: ${customerDetails.gstin}`, 18, Math.min(custY, y + 31));
  }

  // Ship To (if different) - placeholder
  doc.setFillColor(248, 250, 252);
  doc.rect(111, y, 85, 35, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(111, y, 85, 35, 'S');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 100, 100);
  doc.text('SHIP TO:', 115, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Same as billing address', 115, y + 13);

  y += 45;

  // Items Table
  const isInterState = businessDetails.state && customerDetails.state && 
                       businessDetails.state !== customerDetails.state;

  const tableHead = isInterState
    ? [['#', 'Description', 'HSN/SAC', 'Qty', 'Rate', 'IGST', 'Amount']]
    : [['#', 'Description', 'HSN/SAC', 'Qty', 'Rate', 'CGST', 'SGST', 'Amount']];

  const tableBody = items.map((item, index) => {
    const amount = item.quantity * item.rate;
    const gstAmount = (amount * item.gstRate) / 100;
    
    if (isInterState) {
      return [
        index + 1,
        item.description || '-',
        item.hsn || '-',
        item.quantity,
        formatCurrency(item.rate),
        `${formatCurrency(gstAmount)} (${item.gstRate}%)`,
        formatCurrency(amount),
      ];
    } else {
      return [
        index + 1,
        item.description || '-',
        item.hsn || '-',
        item.quantity,
        formatCurrency(item.rate),
        `${formatCurrency(gstAmount / 2)} (${item.gstRate / 2}%)`,
        `${formatCurrency(gstAmount / 2)} (${item.gstRate / 2}%)`,
        formatCurrency(amount),
      ];
    }
  });

  autoTable(doc, {
    startY: y,
    head: tableHead,
    body: tableBody,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: isInterState ? {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 50 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 30, halign: 'right' },
      6: { cellWidth: 30, halign: 'right' },
    } : {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 45 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 12, halign: 'center' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 25, halign: 'right' },
      6: { cellWidth: 25, halign: 'right' },
      7: { cellWidth: 25, halign: 'right' },
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // Totals Section (Right aligned)
  const totalsX = 120;
  const totalsWidth = 76;

  doc.setFillColor(248, 250, 252);
  doc.rect(totalsX, y, totalsWidth, 60, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(totalsX, y, totalsWidth, 60, 'S');

  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  
  let totY = y + 8;
  const drawTotalRow = (label, value, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(label, totalsX + 4, totY);
    doc.text(value, totalsX + totalsWidth - 4, totY, { align: 'right' });
    totY += 7;
  };

  drawTotalRow('Subtotal:', formatCurrency(totals.subtotal));
  if (totals.discountAmount > 0) {
    drawTotalRow(`Discount (${discount.type === 'percent' ? discount.value + '%' : 'Flat'}):`, `-${formatCurrency(totals.discountAmount)}`);
  }
  if (isInterState) {
    drawTotalRow('IGST:', formatCurrency(totals.igst));
  } else {
    drawTotalRow('CGST:', formatCurrency(totals.cgst));
    drawTotalRow('SGST:', formatCurrency(totals.sgst));
  }
  if (totals.shippingCharge > 0) {
    drawTotalRow('Shipping:', formatCurrency(totals.shippingCharge));
  }
  if (totals.packagingCharge > 0) {
    drawTotalRow('Packaging:', formatCurrency(totals.packagingCharge));
  }

  // Grand Total
  doc.setFillColor(37, 99, 235);
  doc.rect(totalsX, totY - 2, totalsWidth, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL:', totalsX + 4, totY + 6);
  doc.text(formatCurrency(totals.grandTotal), totalsX + totalsWidth - 4, totY + 6, { align: 'right' });

  // Amount in Words (Left side)
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Amount in Words:', 14, y + 8);
  doc.setFont('helvetica', 'normal');
  
  const amountWords = numberToWords(totals.grandTotal) + ' Only';
  const splitWords = doc.splitTextToSize(amountWords, 100);
  doc.text(splitWords, 14, y + 15);

  y += 70;

  // Bank Details
  if (bankDetails.bankName || bankDetails.accountNumber) {
    doc.setFillColor(240, 253, 244);
    doc.rect(14, y, 90, 30, 'F');
    doc.setDrawColor(34, 197, 94);
    doc.rect(14, y, 90, 30, 'S');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(22, 163, 74);
    doc.text('Bank Details:', 18, y + 7);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    let bankY = y + 13;
    if (bankDetails.bankName) {
      doc.text(`Bank: ${bankDetails.bankName}`, 18, bankY);
      bankY += 5;
    }
    if (bankDetails.accountNumber) {
      doc.text(`A/C No: ${bankDetails.accountNumber}`, 18, bankY);
      bankY += 5;
    }
    if (bankDetails.ifscCode) {
      doc.text(`IFSC: ${bankDetails.ifscCode}`, 18, bankY);
      bankY += 5;
    }
    if (bankDetails.upiId) {
      doc.text(`UPI: ${bankDetails.upiId}`, 18, bankY);
    }
  }

  // Notes
  if (notes) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Notes:', 14, y + 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const splitNotes = doc.splitTextToSize(notes, 180);
    doc.text(splitNotes, 14, y + 46);
  }

  // Terms & Conditions
  if (terms) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Terms & Conditions:', 14, y + 56);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const splitTerms = doc.splitTextToSize(terms, 180);
    doc.text(splitTerms, 14, y + 61);
  }

  // Signature Section
  y = 260;
  doc.setDrawColor(200, 200, 200);
  doc.line(140, y, 190, y);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('Authorized Signature', 165, y + 6, { align: 'center' });

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('This is a computer-generated invoice.', 105, 285, { align: 'center' });

  // Save
  const fileName = `${invoiceTypeLabels[invoiceType].replace(/\s+/g, '_')}_${invoiceNumber}.pdf`;
  doc.save(fileName);
};

// Helper function to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
